// 基本項目の組み立てと、新規時の既定値/連動値の適用・保存前整形をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createEntryDefaultsService(deps) {
    const {
        CELLTYPE,
        DicVal,
        G_id,
        applyCostRateToEntryData,
        assignEntryDataCurrent,
        columninfo,
        columns,
        controlDateRef,
        dispatch,
        entrydata,
        entrydataRef,
        entryrerender,
        firstNonEmptyValue,
        getEntryDataValue,
        initializedata,
        insertBrandHinbanField,
        insertCostRateField,
        insertFixedHinbanNumberingField,
        insertSubBrandField,
        isEmpty,
        matchesColumnId,
        normalizeDisplayFields,
        rerender,
        resolveBrandHinbanField,
        resolveCostRateField,
        resolveEntryDataKey,
        resolveFixedHinbanNumberingField,
        resolveSubBrandField,
        setEntryDataValue,
        staff,
        textfieldsall
    } = deps;

    function buildBaseFieldSet(colinfo, columns) {
        const basefields = normalizeDisplayFields(colinfo.filter(e => e.logpid === G_id.BASE));
        const visiblefields = normalizeDisplayFields(columns.filter(e => e.logpid === G_id.BASE));
        const subBrandField = resolveSubBrandField(basefields);
        const brandHinbanField = resolveBrandHinbanField(basefields);
        const fixedField = resolveFixedHinbanNumberingField(basefields);
        const costRateField = resolveCostRateField(basefields);
        return {
            all: insertCostRateField(insertFixedHinbanNumberingField(insertBrandHinbanField(insertSubBrandField(basefields, subBrandField), brandHinbanField), fixedField), costRateField),
            visible: insertCostRateField(insertFixedHinbanNumberingField(insertBrandHinbanField(insertSubBrandField(visiblefields, subBrandField), brandHinbanField), fixedField), costRateField)
        };
    }
    function baseFieldsForInit(fields) {
        if (isEmpty(fields)) return [];
        return fields.filter(e => !e.__clientOnly);
    }
    function createBaseInitialData(fields) {
        const data = initializedata(baseFieldsForInit(fields), staff) || {};
        fields.filter(e => e.__clientOnly).forEach((field) => {
            if (!(field.columnid in data)) data[field.columnid] = DicVal(field, "initvalue", "0");
        });
        applyKonpoGrpDefault(data);
        applyCostRateToEntryData(data);
        applyRotRoundRuleToEntryData(data);
        return data;
    }
    function applyKonpoGrpDefault(data) {
        if (isEmpty(data)) return data;
        const konpoGrp = String(DicVal(data, "PDT_KONPOGRP", "")).trim();
        if (isEmpty(konpoGrp) || konpoGrp === "01") {
            data.PDT_KONPOGRP = "01:指定無し";
        }
        return data;
    }
    function extractYearMonthFromControlDate(controlDate) {
        if (isEmpty(controlDate)) return { year: "", month: "" };
        const text = String(controlDate).trim();
        const parts = text.split("/");
        if (parts.length < 2) return { year: "", month: "" };
        const year = Number(parts[0]);
        const month = Number(parts[1]);
        if (!Number.isInteger(year) || !Number.isInteger(month)) return { year: "", month: "" };
        return { year, month };
    }
    function resolvePdtYearFromControlDate(controlDate, season) {
        const { year, month } = extractYearMonthFromControlDate(controlDate);
        if (!Number.isInteger(year) || !Number.isInteger(month)) return "";

        const seasonText = String(isEmpty(season) ? "" : season).trim();
        if (seasonText === "1") {
            if (month >= 1 && month <= 8) return String(year);
            if (month >= 9 && month <= 12) return String(year + 1);
        }
        if (seasonText === "2") {
            if (month >= 1 && month <= 2) return String(year - 1);
            if (month >= 3 && month <= 12) return String(year);
        }
        if (seasonText === "9") {
            return String(year);
        }
        return String(year);
    }
    function applyPdtYearFromControlDate(data, controlDate) {
        // PDT_YEAR キーの有無では判定しない。クリア直後の初期データは初期値を持つ項目の
        // キーしか含まず、キー存在ガードがあるとシーズン変更時の年度再計算が黙ってスキップされる。
        if (isEmpty(data)) return data;
        const year = resolvePdtYearFromControlDate(
            isEmpty(controlDate) ? controlDateRef.current : controlDate,
            DicVal(data, "PDT_SEASONCD", "")
        );
        if (!isEmpty(year)) data.PDT_YEAR = year;
        return data;
    }
    function syncPdtYear(controlDate) {
        if (isEmpty(entrydataRef.current)) return;
        applyPdtYearFromControlDate(entrydataRef.current, controlDate);
        dispatch(entrydata({ ...entrydataRef.current }));
        dispatch(entryrerender());
        rerender();
    }
    function syncPdtBrandCodeAlias(targetData, explicitValue) {
        if (isEmpty(targetData)) return false;
        const brandCode = String(isEmpty(explicitValue)
            ? firstNonEmptyValue(targetData, ["PDT_BRDCODE", "PDT_BRDCODE_cd", "PDT_BRANDCD", "PDT_BRAND", "BRDCODE", "BRANDCD", "BRAND"])
            : explicitValue).trim();
        let updated = false;
        ["PDT_BRDCODE", "PDT_BRDCODE_cd"].forEach((key) => {
            if (String(DicVal(targetData, key, "")) === brandCode) return;
            targetData[key] = brandCode;
            updated = true;
        });
        return updated;
    }
    function applyClientOnlyEntryDefaults(data) {
        const newdata = isEmpty(data) ? {} : { ...data };
        if (!isEmpty(textfieldsall)) {
            textfieldsall.filter(e => e.__clientOnly).forEach((field) => {
                if (!(field.columnid in newdata)) newdata[field.columnid] = DicVal(field, "initvalue", "0");
            });
        }
        syncPdtBrandCodeAlias(newdata);
        applyKonpoGrpDefault(newdata);
        applyCostRateToEntryData(newdata);
        syncMakerHyojyunJyodaiFromPrice(newdata);
        applyRotRoundRuleToEntryData(newdata);
        return newdata;
    }
    function resolveRotRoundRule(flagValue) {
        const normalizedFlag = String(isEmpty(flagValue) ? "" : flagValue).trim();
        if (normalizedFlag === "0") {
            return { locked: true, unchange: true, value: "1", enforceValue: true };
        }
        if (normalizedFlag === "1") {
            return { locked: false, unchange: false, value: "", enforceValue: true };
        }
        return { locked: false, unchange: false, value: "", enforceValue: false };
    }
    function applyRotRoundRuleToEntryData(targetData) {
        const rule = resolveRotRoundRule(getEntryDataValue(targetData, "ROTCHANGEFLG", ""));
        if (isEmpty(targetData) || !rule.enforceValue) return false;
        return setEntryDataValue(targetData, "ROTROUND", rule.value);
    }
    function resolveZaikoHyokaKbnFromShohinKbn(shohinKbn) {
        const mapping = {
            "01": "1",
            "02": "3",
            "03": "1",
            "04": "3",
            "08": "3",
            "09": "3"
        };
        const key = String(isEmpty(shohinKbn) ? "" : shohinKbn).trim();
        return DicVal(mapping, key, "");
    }
    function applyZaikoHyokaKbnFromShohinKbn(targetData) {
        if (isEmpty(targetData)) return false;
        const newValue = resolveZaikoHyokaKbnFromShohinKbn(DicVal(targetData, "PDT_SHOHINKBN", ""));
        if (isEmpty(newValue)) return false;
        if (String(DicVal(targetData, "PDT_ZAIKOHYOKAKBN", "")) === newValue) return false;
        targetData.PDT_ZAIKOHYOKAKBN = newValue;
        return true;
    }
    function syncMakerHyojyunJyodaiFromPrice(targetData) {
        if (isEmpty(targetData)) return false;
        const newValue = String(DicVal(targetData, "PDT_PRICE", ""));
        const key = resolveEntryDataKey(targetData, "PDT_QRC_MAKERHYOJYUNJYODAI");
        if (String(DicVal(targetData, key, "")) === newValue) return false;
        const descriptor = Object.getOwnPropertyDescriptor(targetData, key);
        const writable = isEmpty(descriptor) ? Object.isExtensible(targetData) : descriptor.writable !== false;
        const nextData = writable ? targetData : { ...targetData };
        nextData[key] = newValue;
        if (nextData !== targetData && targetData === entrydataRef.current) {
            assignEntryDataCurrent(nextData, "syncMakerHyojyunJyodaiFromPrice");
        }
        return true;
    }
    function sanitizeEntryData(data) {
        if (isEmpty(data)) return data;
        const newdata = { ...data };
        syncPdtBrandCodeAlias(newdata);
        if (!isEmpty(textfieldsall)) {
            textfieldsall.filter(e => e.__clientOnly).forEach((field) => {
                delete newdata[field.columnid];
            });
        }
        return normalizeNumericPayloadObject(newdata);
    }
    function numericPayloadColumnIds() {
        const columnIds = new Set();
        const addColumnId = (value) => {
            const columnId = String(value ?? "").trim();
            if (columnId !== "") columnIds.add(columnId.toUpperCase());
        };
        [columninfo, textfieldsall, columns].forEach((source) => {
            (Array.isArray(source) ? source : []).forEach((field) => {
                if (String(DicVal(field, "datatype", "")).trim() !== String(CELLTYPE.NUM)) return;
                addColumnId(DicVal(field, "columnid", ""));
                addColumnId(DicVal(field, "dbcolumn", ""));
                addColumnId(DicVal(field, "field", ""));
                addColumnId(DicVal(field, "colId", ""));
            });
        });
        return columnIds;
    }
    function normalizeNumericPayloadObject(data) {
        if (isEmpty(data)) return data;
        const numericColumnIds = numericPayloadColumnIds();
        if (numericColumnIds.size === 0) return data;
        const next = { ...data };
        Object.keys(next).forEach((key) => {
            if (!numericColumnIds.has(String(key).trim().toUpperCase())) return;
            if (typeof next[key] === "string") next[key] = next[key].replace(/,/g, "");
        });
        return next;
    }
    function createJyutakuHasuShoriSaveColumnInfo(columnid, columnnm) {
        return {
            table: "M_PDT",
            columnid: columnid,
            columnnm: columnnm,
            datatype: "1",
            dataid: "",
            dataidctrl: "",
            dbcolumn: "",
            groupcolumn: "",
            format: "",
            formula: "",
            sql: "",
            searchtype: "",
            hidden: "2",
            maxvalue: "",
            minvalue: "",
            expression: "",
            constraint: "",
            keyinfo: "",
            initvalue: "",
            acceptfield: "",
            type: "0",
            logpid: G_id.BASE,
            saveed: true
        };
    }
    function ensureSaveColumnInfoField(fields, columnid, columnnm) {
        if (!Array.isArray(fields)) return fields;
        const exists = fields.some((field) => matchesColumnId(DicVal(field, "columnid", ""), columnid));
        if (exists) return fields;
        return fields.concat([createJyutakuHasuShoriSaveColumnInfo(columnid, columnnm)]);
    }
    function buildSaveColumnInfo(sourceColumnInfo) {
        const normalized = (Array.isArray(sourceColumnInfo) ? sourceColumnInfo : []).map((field) => {
            if (String(DicVal(field, "columnid", "")).trim().toUpperCase() !== "PDT_RENBAN") return field;
            if (String(DicVal(field, "dataid", "")).trim().toUpperCase() !== "UNUSENO") return field;
            return {
                ...field,
                dataid: ""
            };
        });
        return ensureSaveColumnInfoField(
            ensureSaveColumnInfoField(normalized, "PDT_JYUHASUPOSITION", "受託品端数処理位置"),
            "PDT_JYUHASUKBN",
            "受託品端数処理区分"
        );
    }

    return {
        buildBaseFieldSet,
        baseFieldsForInit,
        createBaseInitialData,
        applyKonpoGrpDefault,
        extractYearMonthFromControlDate,
        resolvePdtYearFromControlDate,
        applyPdtYearFromControlDate,
        syncPdtYear,
        syncPdtBrandCodeAlias,
        applyClientOnlyEntryDefaults,
        resolveRotRoundRule,
        applyRotRoundRuleToEntryData,
        resolveZaikoHyokaKbnFromShohinKbn,
        applyZaikoHyokaKbnFromShohinKbn,
        syncMakerHyojyunJyodaiFromPrice,
        sanitizeEntryData,
        numericPayloadColumnIds,
        normalizeNumericPayloadObject,
        createJyutakuHasuShoriSaveColumnInfo,
        ensureSaveColumnInfoField,
        buildSaveColumnInfo
    };
}
